import type { Emitter } from "@core/events/emitter";

/**
 * `IDebugModeModel` — the app-wide developer-affordance gate, toggled by typing
 * the hidden command `potato` into the Spark prompt.
 *
 * **Web-only — there is no Unity counterpart.** The desktop client gates its one
 * comparable surface on `IAppConfig.SceneStatisticsEnabled`, a build-time config
 * flag that ships false; the web port has no `IAppConfig`, so the equivalent
 * developer surfaces were simply always visible. This model is that gate,
 * expressed as something a developer can flip at runtime on any build.
 *
 * What it hides while OFF (the default):
 *   - the scene-statistics toggle in `SceneActionsBar` (and, transitively, the
 *     panel itself — `useSceneStatistics.isOpen` folds this flag in, so a panel
 *     left open in a previous session cannot arrive with no way to dismiss it);
 *   - the Workflow editor's `dev` switch, and with it the raw-YAML editor it
 *     opens (`useWorkflow.devMode` folds this flag in for the same reason);
 *   - the AI debug dialog and its toggle (`useAiDebug.isOpen`), including its
 *     "Settings" tab — and, through `IDevSettingsModel`, the EFFECT of
 *     every override set there, so an override can never outlive the only
 *     dialog that can undo it;
 *   - the per-tool-call cards in the Spark feed — but as an OR with Spark's own
 *     debug preference, not an AND; see below.
 *
 * **Why this is an IOC model and not a module-scoped composable.** The flag is
 * written from one surface (`useSpark`, the Spark prompt) and read from several
 * unrelated ones — a component (`SceneActionsBar`), module-singleton
 * composables (`useSceneStatistics`, `useSpark`), and a `globalThis`-anchored
 * singleton (`useWorkflow`). A module-scoped ref would be duplicated by a Vite
 * HMR module swap and the readers would split across the copies, which is the
 * exact bug `useWorkflow`'s own singleton note records. Every reader syncing
 * FROM one IOC instance is immune to that: a duplicated composable module still
 * subscribes to the same model.
 *
 * **Deliberately NOT the same flag as Spark's debug mode.** `AIInterfaceModel`
 * owns `Campfire.AI.DebugMode` (tool cards + the usage row in the Spark panel)
 * — a faithful Unity port that the agent itself can flip via the
 * `Toggle_DebugMode` tool. `Campfire_AI_Harness.md` scopes that one to
 * INTERFACE-only preferences, and editor chrome is not interface state; folding
 * the two together would also let a tool call reveal or hide editor
 * affordances. They stay independent AS FLAGS.
 *
 * They do meet in exactly one place, by user direction (2026-08-10): the Spark
 * feed's tool cards render under `useSpark.isToolActivityVisible`, the OR of
 * the two. An OR, and derived in the ViewModel rather than in either model,
 * because the flags have different owners and neither should overrule the
 * other — a `Toggle_DebugMode` call must not be able to close a surface the
 * developer opened with `potato`, and vice versa.
 */
export interface IDebugModeModel {
  /** Fires whenever the flag changes. Payload is the new value. */
  readonly onChanged: Emitter<boolean>;

  /** False by default, including on a first run with no stored preference. */
  readonly isEnabled: boolean;

  setEnabled(enabled: boolean): void;

  /** Flips the flag and returns the NEW value, so a caller can report it. */
  toggle(): boolean;
}

export const DEBUG_MODE_MODEL = Symbol.for("Campfire.DebugModeModel");

/**
 * The hidden command that toggles the flag. Matched case-insensitively against
 * the trimmed prompt text in `useSpark.sendText`, so it is never sent to Spark.
 */
export const DEBUG_MODE_COMMAND = "potato";
